// This import loads the firebase namespace along with all its type information.
import {
  getDownloadURL,
  getStorage,
  ref,
  uploadBytesResumable,
} from '@firebase/storage'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { uuid } from 'components/util'
import { add_progress, set_progress } from 'features/progress/progress-slice'

export const UploadThunkAsync = createAsyncThunk(
  'upload/thunk/async',
  async (params: any, thunkAPI) => {
    let { dispatch } = thunkAPI
    let { files } = params
    let result: any = {}
    for (let i = 0; i < files.length; i++) {
      let { id, storageLocation, file, name } = files[i]
      dispatch(
        add_progress({
          id,
          name,
          state: '',
          value: 0,
        }),
      )
      await uploadFileToStorage({
        id,
        storage_location: storageLocation,
        file: file,
        state_change_callback: (snapshot, id) => {
          // Observe state change events such as progress, pause, and resume
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          const progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          console.log('Upload is ' + progress + '% done')
          switch (snapshot.state) {
            case 'paused':
              console.log('Upload is paused')
              break
            case 'running':
              console.log('Upload is running')
              break
          }
          dispatch(
            set_progress({
              id,
              key: 'state',
              value: snapshot.state,
            }),
          )
          dispatch(
            set_progress({
              id,
              key: 'value',
              value: progress,
            }),
          )
          if (params?.onprogress) {
            params?.onprogress({
              id,
              key: 'value',
              value: progress,
            })
          }
        },
        on_error: (error, id) => {
          dispatch(
            set_progress({
              id,
              key: 'state',
              value: 'failed',
            }),
          )
        },
        on_success: (url, id, storageLocation) => {
          dispatch(
            set_progress({
              id,
              key: 'value',
              value: 1,
            }),
          )
          result[id] = {
            id,
            storageLocation,
            url,
          }
        },
      })
    }
    if (params?.callback) {
      params?.callback(true, result)
    }

    return result
  },
)

export async function uploadFileToStorage(args: {
  id: string
  storage_location: string
  file: Blob | Uint8Array | ArrayBuffer
  state_change_callback: (snapshot: any, id: any) => void
  on_error: (error: any, id: any) => void
  on_success: (url: any, id: any, storageLocation: any) => void
}) {
  let {
    id,
    storage_location,
    file,
    state_change_callback,
    on_error,
    on_success,
  } = args
  try {
    const storage = getStorage()
    const _storage_location_ = `${storage_location}/${uuid()}`
    const storageRef = ref(storage, _storage_location_)

    // 'file' comes from the Blob or File API
    let uploadTask = uploadBytesResumable(storageRef, file)
    // Register three observers:
    // 1. 'state_changed' observer, called any time the state changes
    // 2. Error observer, called on failure
    // 3. Completion observer, called on successful completion
    let success = false
    uploadTask.on(
      'state_changed',
      (snapshot) => {
        if (state_change_callback) {
          state_change_callback(snapshot, id)
        }
      },
      (error) => {
        // Handle unsuccessful uploads
        if (on_error) {
          on_error(error, id)
        }
      },
      () => {
        // Handle successful uploads on complete
        // For instance, get the download URL: https://firebasestorage.googleapis.com/...
        success = true
      },
    )
    await uploadTask
    if (on_success) {
      let url = await getDownloadURL(uploadTask.snapshot.ref).then(
        (downloadURL) => {
          console.log('File available at', downloadURL)
          return downloadURL
        },
      )
      on_success(url, id, _storage_location_)
    }
  } catch (e) {
    console.log(e)
  }
}
